package org.multiplatform.kotfolio.pages.kotlin.design_pattern

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/kotlin/design_pattern/design_patterns")
@Composable
fun DesignPatternsPage() {
    BlogLayout(
        PageProperties(
            "Design Pattern",
            ""
        ),
        BlogProperties("kotlin", "Design Pattern")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Singleton Pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Singleton Pattern restricts a class to only one instance, providing global access to that instance. It’s ideal for cases where one instance is needed across the entire application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    object Singleton {
                        fun showMessage() = println("Singleton instance")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Factory Pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Factory Pattern defines a way to create objects while hiding the creation logic. This pattern is useful when creating multiple related classes through a common interface.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    abstract class Animal {
                        abstract fun sound(): String
                    }

                    class Dog : Animal() {
                        override fun sound() = "Woof"
                    }

                    class Cat : Animal() {
                        override fun sound() = "Meow"
                    }

                    object AnimalFactory {
                        fun createAnimal(type: String): Animal? = when (type) {
                            "Dog" -> Dog()
                            "Cat" -> Cat()
                            else -> null
                        }
                    }

                    // Usage
                    val dog = AnimalFactory.createAnimal("Dog")
                    println(dog?.sound())  // Output: Woof
                    """.trimIndent()
            )

            SpanText(
                text = "Observer Pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Observer Pattern creates a one-to-many dependency between objects, where a subject notifies observers about any state changes. This is particularly useful for event-driven architectures.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    interface Observer {
                        fun update(message: String)
                    }

                    class ConcreteObserver(private val name: String) : Observer {
                        override fun update(message: String) {
                            println(name)
                            println("message)
                        }
                    }

                    class Subject {
                        private val observers = mutableListOf<Observer>()

                        fun addObserver(observer: Observer) {
                            observers.add(observer)
                        }

                        fun notifyObservers(message: String) {
                            observers.forEach { it.update(message) }
                        }
                    }

                    // Usage
                    val subject = Subject()
                    val observer1 = ConcreteObserver("Observer1")
                    subject.addObserver(observer1)
                    subject.notifyObservers("Hello Observers!")  // Observer1 received: Hello Observers!
                    """.trimIndent()
            )

            SpanText(
                text = "Strategy Pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Strategy Pattern defines a family of algorithms, encapsulating each algorithm within a class and making them interchangeable. This pattern is beneficial when a system requires different behaviors at runtime.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    interface Strategy {
                        fun doOperation(num1: Int, num2: Int): Int
                    }

                    class OperationAdd : Strategy {
                        override fun doOperation(num1: Int, num2: Int) = num1 + num2
                    }

                    class OperationMultiply : Strategy {
                        override fun doOperation(num1: Int, num2: Int) = num1 * num2
                    }

                    class Context(private val strategy: Strategy) {
                        fun executeStrategy(num1: Int, num2: Int) = strategy.doOperation(num1, num2)
                    }

                    // Usage
                    val context = Context(OperationAdd())
                    println(context.executeStrategy(5, 3))  // Output: 8
                    """.trimIndent()
            )

            SpanText(
                text = "Builder Pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Builder Pattern separates object construction from its representation, allowing for complex objects to be created step-by-step. This is especially useful for objects with multiple configurations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class Computer private constructor(
                        val CPU: String?,
                        val RAM: String?,
                        val storage: String?
                    ) {
                        data class Builder(
                            var CPU: String? = null,
                            var RAM: String? = null,
                            var storage: String? = null
                        ) {
                            fun setCPU(CPU: String) = apply { this.CPU = CPU }
                            fun setRAM(RAM: String) = apply { this.RAM = RAM }
                            fun setStorage(storage: String) = apply { this.storage = storage }
                            fun build() = Computer(CPU, RAM, storage)
                        }
                    }

                    // Usage
                    val computer = Computer.Builder().setCPU("Intel").setRAM("16GB").setStorage("1TB").build()
                    """.trimIndent()
            )
        }
    }
}
